import {
  ORGANIZATION_UPDATED,
  RACI_LETTER_LABELS,
  RACI_LETTER_MEANINGS,
  RACI_LETTERS,
  RACI_MATRIX,
  RACI_ROLES,
  type RaciActivity,
  type RaciCell,
  type RaciLetter,
  type RaciRole,
} from '@k-management/shared';
import { useEffect, useState } from 'react';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { formatDate } from '../../lib/format.js';

interface Tip {
  letter: RaciLetter;
  role: RaciRole;
  activity: RaciActivity;
  cell: RaciCell;
  /** Where the letter is on the screen. */
  rect: DOMRect;
}

const roleLabel = (key: RaciRole) => RACI_ROLES.find((role) => role.key === key)!.label;

const SENTENCES: Record<RaciLetter, (role: string) => string> = {
  R: (role) => `${role} esegue questa attività.`,
  A: (role) => `${role} risponde del risultato.`,
  C: (role) => `${role} va consultato prima.`,
  I: (role) => `${role} viene informato.`,
};

/**
 * Organizzazione › Responsabilità (03/10/2026): the RACI matrix, activities by
 * phase and roles in columns. Hovering or tapping a letter explains it.
 */
export function ResponsibilitiesPage() {
  usePageTitle('Responsabilità');
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [onlyInApp, setOnlyInApp] = useState(true);
  const [highlighted, setHighlighted] = useState<RaciRole | ''>('');
  const [tip, setTip] = useState<Tip | null>(null);

  // The explanation follows the letter: it closes when the page scrolls.
  useEffect(() => {
    if (!tip) return;
    const close = () => setTip(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [tip]);

  const phases = RACI_MATRIX.map((phase) => ({
    ...phase,
    activities: phase.activities.filter((activity) => !onlyInApp || activity.inApp),
  })).filter((phase) => phase.activities.length > 0);

  const toggle = (title: string) =>
    setClosed((current) => {
      const next = new Set(current);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  const show = (target: HTMLElement, value: Omit<Tip, 'rect'>) =>
    setTip({ ...value, rect: target.getBoundingClientRect() });

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Responsabilità</h1>
          <p className="muted">
            Per ogni attività: chi la esegue, chi ne risponde, chi va consultato e chi informato.
            Aggiornata al {formatDate(ORGANIZATION_UPDATED)}.
          </p>
        </div>
      </div>

      <div className="card stack">
        <div className="org-toolbar">
          <div className="org-raci-legend">
            {RACI_LETTERS.map((letter) => (
              <span key={letter}>
                <i className={`org-letter ${letter}`}>{letter}</i> {RACI_LETTER_LABELS[letter]}
              </span>
            ))}
            <span>
              <i className="org-letter R hypothesis">R</i> da validare
            </span>
          </div>
          <span className="org-spacer" />
          <button type="button" onClick={() => setClosed(new Set())}>
            Espandi tutto
          </button>
          <button
            type="button"
            onClick={() => setClosed(new Set(RACI_MATRIX.map((phase) => phase.title)))}
          >
            Comprimi tutto
          </button>
          <select
            aria-label="Evidenzia un ruolo"
            value={highlighted}
            onChange={(event) => setHighlighted(event.target.value as RaciRole | '')}
          >
            <option value="">Evidenzia un ruolo…</option>
            {RACI_ROLES.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
              </option>
            ))}
          </select>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={onlyInApp}
              onChange={(event) => setOnlyInApp(event.target.checked)}
            />
            Solo attività in K-Management
          </label>
        </div>

        <div className="table-wrap">
          <table className="data-table org-raci">
            <thead>
              <tr>
                <th>#</th>
                <th>Attività</th>
                {RACI_ROLES.map((role) => (
                  <th
                    key={role.key}
                    className={`org-raci-role${highlighted === role.key ? ' highlighted' : ''}`}
                  >
                    <button
                      type="button"
                      className="link"
                      title="Evidenzia la colonna"
                      onClick={() => setHighlighted(highlighted === role.key ? '' : role.key)}
                    >
                      {role.label}
                    </button>
                  </th>
                ))}
                <th>Blocchi</th>
              </tr>
            </thead>
            {phases.map((phase) => {
              const open = !closed.has(phase.title);
              return (
                <tbody key={phase.title}>
                  <tr className="org-raci-phase">
                    <td colSpan={RACI_ROLES.length + 3}>
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggle(phase.title)}
                      >
                        {open ? '▾' : '▸'} {phase.title}
                      </button>
                    </td>
                  </tr>
                  {open &&
                    phase.activities.map((activity) => (
                      <tr key={activity.number} className={activity.inApp ? undefined : 'inactive'}>
                        <td className="muted small">{activity.number}</td>
                        <td className="org-raci-activity">
                          {activity.activity}
                          {!activity.inApp && (
                            <span className="pill muted"> fuori da K-Management</span>
                          )}
                        </td>
                        {RACI_ROLES.map((role) => {
                          const cell = activity.cells[role.key];
                          return (
                            <td
                              key={role.key}
                              className={`org-raci-cell${highlighted === role.key ? ' highlighted' : ''}`}
                            >
                              {cell && (
                                <div className="org-raci-letters">
                                  <div>
                                    {cell.letters.map((letter) => (
                                      <button
                                        key={letter}
                                        type="button"
                                        className={`org-letter ${letter}${cell.hypothesis ? ' hypothesis' : ''}`}
                                        aria-label={`${letter}: ${RACI_LETTER_LABELS[letter]}`}
                                        onMouseEnter={(event) =>
                                          show(event.currentTarget, {
                                            letter,
                                            role: role.key,
                                            activity,
                                            cell,
                                          })
                                        }
                                        onFocus={(event) =>
                                          show(event.currentTarget, {
                                            letter,
                                            role: role.key,
                                            activity,
                                            cell,
                                          })
                                        }
                                        onMouseLeave={() => setTip(null)}
                                        onBlur={() => setTip(null)}
                                        onClick={(event) =>
                                          show(event.currentTarget, {
                                            letter,
                                            role: role.key,
                                            activity,
                                            cell,
                                          })
                                        }
                                      >
                                        {letter}
                                      </button>
                                    ))}
                                  </div>
                                  {cell.note && <small>{cell.note}</small>}
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="org-raci-block">{activity.block}</td>
                      </tr>
                    ))}
                </tbody>
              );
            })}
          </table>
        </div>
        <p className="muted small">
          Il sostituto subentra al titolare solo durante un’assenza registrata. Passa sopra una
          lettera, o toccala, per la spiegazione.
        </p>
      </div>

      {tip && <LetterTip tip={tip} />}
    </section>
  );
}

function LetterTip({ tip }: { tip: Tip }) {
  const width = 300;
  const left = Math.min(
    window.innerWidth - width - 12,
    Math.max(12, tip.rect.left + tip.rect.width / 2 - width / 2),
  );
  // Below the letter, or above when there is no room.
  const below = tip.rect.bottom + 180 < window.innerHeight;
  const style = below
    ? { left, top: tip.rect.bottom + 8, width }
    : { left, bottom: window.innerHeight - tip.rect.top + 8, width };
  return (
    <div className="org-tip" role="tooltip" style={style}>
      <strong>
        <i className={`org-letter ${tip.letter}`}>{tip.letter}</i> {RACI_LETTER_LABELS[tip.letter]}
      </strong>
      <div className="muted small">
        {tip.activity.number} · {tip.activity.activity}
      </div>
      <div>{SENTENCES[tip.letter](roleLabel(tip.role))}</div>
      <div className="muted">{RACI_LETTER_MEANINGS[tip.letter]}</div>
      {tip.cell.note && (
        <div>
          {tip.cell.note.startsWith('sostituto: ')
            ? `In sua assenza: ${tip.cell.note.slice('sostituto: '.length)}.`
            : `Nota: ${tip.cell.note}.`}
        </div>
      )}
      {tip.cell.hypothesis && (
        <div className="org-tip-warn">Ipotesi ancora da validare: per ora non si applica.</div>
      )}
    </div>
  );
}
